import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

export const FeedOrderStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'COMPLETED', 'CANCELLED', 'ON_HOLD', 'PENDING']);
export type FeedOrderStatus = z.infer<typeof FeedOrderStatusSchema>;

export const FeedOrderTypeSchema = z.enum(['Standard', 'NPO', 'Oral Care']);
export type FeedOrderType = z.infer<typeof FeedOrderTypeSchema>;

export const FeedOrderRouteSchema = z.enum([
  'SNG (Nasogastric)', 'VO (Oral/Per Os)', 'VGNS (Nasogastric)', 'OG (Orogastric)', 'PEG', 'Gavage',
]);
export type FeedOrderRoute = z.infer<typeof FeedOrderRouteSchema>;

export const FeedOrderBaseSchema = z.enum([
  "Mother's Milk Only",
  'Donor Milk',
  'Nutrition Product/Formula',
  'Mixed (MOM + Donor fallback)',
]);
export type FeedOrderBase = z.infer<typeof FeedOrderBaseSchema>;

export const FeedFrequencySchema = z.enum([
  'Every 2h', 'Every 3h', 'Every 4h', 'Every 6h',
]);
export type FeedFrequency = z.infer<typeof FeedFrequencySchema>;

/**
 * Output schema — what the API returns for a feed order row.
 * Prisma Decimal/Date fields are converted to strings in the service layer.
 */
export const FeedOrderSchema = z.object({
  order_id:             uuidSchema,
  order_identifier:     z.string().max(20).nullable(),
  encounter_id:         uuidSchema,
  patient_id:           uuidSchema,
  hospital_id:          uuidSchema,
  ordered_by:           uuidSchema,
  transcribed_by:       uuidSchema.nullable(),
  order_base:           FeedOrderBaseSchema.nullable(),
  order_type:           FeedOrderTypeSchema,
  recipe_id:            uuidSchema.nullable(),
  volume_ml_per_feed:   z.string().nullable(),
  calorific_density:    z.string().nullable(),
  feed_frequency:       FeedFrequencySchema.nullable(),
  route:                FeedOrderRouteSchema.nullable(),
  milk_type:            z.string().max(50).nullable(),
  special_instructions: z.string().nullable(),
  start_date:           z.string(),
  end_date:             z.string().nullable(),
  status:               FeedOrderStatusSchema,
  // Why this order was cancelled — system-generated for edit/reorder, user-typed for an
  // explicit remove or panel-staged Cancel. Never special_instructions (see repo.deactivate()).
  cancellation_reason:  z.string().nullable(),
  // The order this row supersedes (edit/reorder), or null for a plain create and for
  // panel-staged Cancels (the new order is not their replacement).
  replaces_order_id:    uuidSchema.nullable(),
  created_at:           z.string(),
  updated_at:           z.string(),
});
export type FeedOrder = z.infer<typeof FeedOrderSchema>;

/**
 * Query params for GET /feed-orders
 *
 * Filters:  order_type, route (feed method), volume, feed_frequency
 * Search:   search — cross-field OR across patient first_name, last_name, mrn, and order_identifier
 */
export const FeedOrderListQuerySchema = z.object({
  // Filter: a single patient's orders (per-patient panel, duplicate-order check).
  patient_id:       uuidSchema.optional(),
  // Filter: a single encounter's orders.
  encounter_id:     uuidSchema.optional(),
  // Filter: order status (array); accepts a single value or multiple values.
  // A single query param (?status=PENDING) is coerced to an array automatically.
  status:           z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(FeedOrderStatusSchema).optional(),
  ),
  // Filter: order type (array); accepts a single value or multiple values.
  // A single query param (?order_type=NPO) is coerced to an array automatically.
  order_type:       z.preprocess(
    (val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
    z.array(FeedOrderTypeSchema).optional(),
  ),
  // Filter: feed method / route
  route:            FeedOrderRouteSchema.optional(),
  // Filter: volume (ml per feed)
  volume_ml:        z.coerce.number().positive().optional(),
  // Filter: feed frequency
  feed_frequency:   FeedFrequencySchema.optional(),
  // Search: unified cross-field search (patient name, MRN, order identifier)
  search:           z.string().min(1).max(100).optional(),
  // Pagination — both optional; omitted preserves today's unpaginated (full-array) behaviour.
  page:             z.coerce.number().int().positive().optional(),
  limit:            z.coerce.number().int().positive().max(100).optional(),
});
export type FeedOrderListQuery = z.infer<typeof FeedOrderListQuerySchema>;

/** Params schema for GET /feed-orders/:order_id */
export const FeedOrderParamsSchema = z.object({
  order_id: uuidSchema,
});
export type FeedOrderParams = z.infer<typeof FeedOrderParamsSchema>;

/**
 * Detail shape — FeedOrder enriched with patient identifiers (including middle_name) and order items.
 */
export const FeedOrderDetailSchema = FeedOrderSchema.extend({
  patient: z.object({
    first_name:  z.string(),
    middle_name: z.string().nullable(),
    last_name:   z.string(),
    mrn:         z.string(),
  }).nullable(),
  items: z.array(
    z.object({
      item_id:       z.string(),
      product_id:    z.string(),
      quantity:      z.number(),
      quantity_unit: z.string(),
      notes:         z.string().nullable(),
      local_name:    z.string().nullable().optional(),
      brand_name:    z.string().nullable().optional(),
      form_name:     z.string().nullable().optional(),
      sku:           z.string().nullable().optional(),
    }),
  ).nullable(),
  order_has_feeds: z.boolean(),
});
export type FeedOrderDetail = z.infer<typeof FeedOrderDetailSchema>;

/**
 * List-item shape — FeedOrder enriched with patient identifiers and order items for display.
 */
export const FeedOrderListItemSchema = FeedOrderSchema.extend({
  patient: z.object({
    first_name:  z.string(),
    middle_name: z.string().nullable(),
    last_name:   z.string(),
    mrn:         z.string(),
  }).nullable(),
  feed_order_items: z.array(
    z.object({
      item_id:       z.string(),
      product_id:    z.string(),
      quantity:      z.number(),
      quantity_unit: z.string(),
      notes:         z.string().nullable(),
      local_name:    z.string().nullable().optional(),
      brand_name:    z.string().nullable().optional(),
      form_name:     z.string().nullable().optional(),
      sku:           z.string().nullable().optional(),
    }),
  ).optional(),
});
export type FeedOrderListItem = z.infer<typeof FeedOrderListItemSchema>;

/**
 * Request body for POST /feed-orders
 *
 * When order_type is 'NPO', only start_date (plus identity fields) is mandatory.
 * For Standard / Oral Care, order_base and volume_ml_per_feed are required. calorific_density is always optional.
 */
// Helper: treat empty string as undefined (frontend sends "" for unset fields).
// Uses union+transform rather than z.preprocess — Zod v4's preprocess() resolves the
// resulting z.input<> type as an opaque, unconstrained type instead of the wrapped
// schema's own input type, which silently degrades every field using it (order_base,
// feed_frequency, etc.) to `{}` in CreateFeedOrder and breaks tsc (caught by `pnpm nx
// build`, not by Vitest/Jest's non-typechecking transpilation — hence unnoticed until now).
const emptyToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.union([z.literal(''), schema]).transform((v) => (v === '' ? undefined : v));
// Helper: treat 0 or empty as undefined (frontend sends 0 for unset numbers)
const zeroToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.union([z.literal(0), z.literal(''), schema]).transform((v) => (v === 0 || v === '' ? undefined : v));

export const CreateFeedOrderSchema = z.object({
  original_order_id:    uuidSchema.optional(),
  // Discriminates edit() into a plain field-edit vs. a reorder — isMinorChange can't be used for
  // this (a no-field-change reorder still computes as minor). For REORDER, the submitted
  // start_date also becomes the cancelled original's end_date (see feedOrder.service.ts
  // edit()'s effectiveAt), so the two orders always meet with no gap or overlap.
  order_action:         z.enum(['EDIT', 'REORDER']).default('EDIT'),
  encounter_id:         uuidSchema,
  patient_id:           uuidSchema,
  hospital_id:          uuidSchema,
  ordered_by:           uuidSchema,
  order_type:           FeedOrderTypeSchema,
  start_date:           z.string().datetime(),
  // Fields required for Standard / Oral Care, optional for NPO
  order_base:           emptyToUndefined(FeedOrderBaseSchema.optional()),
  volume_ml_per_feed:   zeroToUndefined(z.number().positive().optional()),
  calorific_density:    zeroToUndefined(z.number().positive().optional()),
  feed_frequency:       emptyToUndefined(FeedFrequencySchema.optional()),
  route:                emptyToUndefined(FeedOrderRouteSchema.optional()),
  milk_type:            emptyToUndefined(z.string().max(50).optional()),
  // Always optional
  recipe_id:            uuidSchema.nullable().optional(),
  special_instructions: z.string().max(1000).optional().nullable(),
  items:                z.array(z.object({
    product_id:    uuidSchema,
    quantity:      z.number().positive(),
    quantity_unit: z.enum(['ml', 'g', 'sachet', 'unit', 'drops']),
  })).default([]),
  // Orders to cancel alongside this create/edit — panel-staged Cancels (Q2/Q5 UI), each with its
  // own optional clinician-typed reason (distinct staged Cancels in one submission may give
  // different reasons, or none — per product decision, documenting a reason is the hospital's
  // choice, not mandatory). Every order_id is validated server-side (patient_id/hospital_id/status
  // match) before any write — see create()'s IDOR guard.
  cancel_order_ids:     z.array(z.object({
    order_id: uuidSchema,
    reason:   z.string().max(1000).optional(),
  })).max(20).optional(),
}).superRefine((data, ctx) => {
  if (data.order_type !== 'NPO') {
    const required: Array<{ field: string; value: unknown }> = [
      { field: 'order_base',         value: data.order_base },
      { field: 'volume_ml_per_feed', value: data.volume_ml_per_feed },
    ];
    for (const { field, value } of required) {
      if (value === undefined || value === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} is required for ${data.order_type} orders`,
        });
      }
    }
  }
});
// z.input (not z.infer/z.output) so fields with .default() — order_action, items — stay optional
// for callers, matching what the server actually accepts. z.infer reads the *post-default*
// output shape, which makes a .default()-only field required even though every caller may omit
// it; that mismatch is what broke feed-administration.api.spec.ts's CreateFeedOrderRequest
// fixture when order_action was added here (SV-160).
export type CreateFeedOrder = z.input<typeof CreateFeedOrderSchema>;

/**
 * Request body for PATCH /feed-orders/:order_id/remove
 */
export const RemoveFeedOrderSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type RemoveFeedOrder = z.infer<typeof RemoveFeedOrderSchema>;

/**
 * Response for POST /feed-orders — includes optional allergen warnings.
 */
export const CreateFeedOrderResponseSchema = FeedOrderSchema.extend({
  warnings: z.array(z.object({
    allergen_id:   uuidSchema,
    allergen_code: z.string(),
    allergen_name: z.string(),
    product_id:    z.string(),
    product_name:  z.string(),
    system_action: z.string(),
  })).nullable(),
});
export type CreateFeedOrderResponse = z.infer<typeof CreateFeedOrderResponseSchema>;

// ── Allergen Verification endpoint ──────────────────────────────────────────

/** A single product+lot pair submitted for allergen verification. */
export const VerifyProductItemSchema = z.object({
  product_id: uuidSchema,
  lot_id:     z.string().min(1).max(100),
});
export type VerifyProductItem = z.infer<typeof VerifyProductItemSchema>;

/** Request body for POST /api/v1/verifyProducts */
export const VerifyProductsRequestSchema = z.object({
  patient_id:  uuidSchema,
  hospital_id: uuidSchema,
  products:    z.array(VerifyProductItemSchema).min(1),
});
export type VerifyProductsRequest = z.infer<typeof VerifyProductsRequestSchema>;

/** A single allergen conflict enriched with lot_id for traceability. */
export const VerifyConflictSchema = z.object({
  allergen_id:   uuidSchema,
  allergen_code: z.string(),
  allergen_name: z.string(),
  product_id:    uuidSchema,
  lot_id:        z.string(),
  product_name:  z.string(),
  system_action: z.string(),
});
export type VerifyConflict = z.infer<typeof VerifyConflictSchema>;

/** An expired product+lot pair. */
export const ExpiredItemSchema = z.object({
  product_id:  uuidSchema,
  lot_id:      z.string(),
  expiry_date: z.string(),
});
export type ExpiredItem = z.infer<typeof ExpiredItemSchema>;

/** A recalled product+lot pair. */
export const RecalledItemSchema = z.object({
  product_id:       uuidSchema,
  lot_id:           z.string(),
  recall_id:        uuidSchema,
  recall_reference: z.string(),
  reason:           z.string(),
});
export type RecalledItem = z.infer<typeof RecalledItemSchema>;

/** Consolidated response shape for POST /verify-products */
export const VerifyProductsResponseSchema = z.object({
  passed:     z.boolean(),
  hard_stops: z.array(VerifyConflictSchema),
  warnings:   z.array(VerifyConflictSchema),
  expired:    z.array(ExpiredItemSchema),
  recalled:   z.array(RecalledItemSchema),
});
export type VerifyProductsResponse = z.infer<typeof VerifyProductsResponseSchema>;

// ── Lot Verification endpoint ────────────────────────────────────────────────

/** Request body for POST /api/v1/verifyLot */
export const VerifyLotRequestSchema = z.object({
  product_id:   uuidSchema,
  batch_number: z.string().min(1).max(100),
  hospital_id:  uuidSchema,
  patient_id:   uuidSchema,
});
export type VerifyLotRequest = z.infer<typeof VerifyLotRequestSchema>;

/** Response shape for POST /api/v1/verifyLot */
export const VerifyLotResponseSchema = z.object({
  product_id:   z.string(),
  batch_number: z.string(),
  hospital_id:  z.string(),
  passed:       z.boolean(),
  allergen: z.object({
    passed:    z.boolean(),
    hardStops: z.array(VerifyConflictSchema.omit({ lot_id: true })),
    warnings:  z.array(VerifyConflictSchema.omit({ lot_id: true })),
  }),
  recall: z.object({
    passed:           z.boolean(),
    severity:         z.enum(['NONE', 'WARNING', 'HARD_STOP']),
    recall_id:        z.string().nullable(),
    recall_title:     z.string().nullable(),
    recall_reference: z.string().nullable(),
  }),
});
export type VerifyLotResponse = z.infer<typeof VerifyLotResponseSchema>;
